import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { ROUTE_DEFINITIONS } from '@/routes';
import { useMonthlyCutDateRangeLookup } from './useMonthlyCutDateRangeLookup';


export interface UseMonthSummaryMarksDetailNavigationResult {
  navigateToDetail: () => Promise<void>;
}

// Data hook for the "Resumen del mes" dashboard "Marcajes" card "Detalle" action
// (ASIS-5399 / REXASIS-008). Redirects to "Mis registros de asistencia" with the date range
// of the cutoff selected in the dashboard. useMyAttendance already reads ?from=&to= (added
// for the "Marcajes de hoy" card in ASIS-5393), so no changes are needed on that side.
export function useMonthSummaryMarksDetailNavigation(
  selectedCompanyId: string,
  selectedCutId: string,
): UseMonthSummaryMarksDetailNavigationResult {
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const { showNotification, showApiError } = useUI();
  const { resolveDateRange } = useMonthlyCutDateRangeLookup();

  const navigateToDetail = useCallback(async () => {
    const { range, response } = await resolveDateRange(selectedCompanyId, selectedCutId);
    if (!range) {
      const fallback = translate('attendance:dashboard.monthSummary.marcajes.menu.loadError');
      if (response) {
        showApiError(response, fallback);
      } else {
        showNotification('error', fallback, 5000);
      }
      return;
    }
    void navigate(`${ROUTE_DEFINITIONS.INICIO.path}?from=${range.from}&to=${range.to}`);
  }, [
    navigate, resolveDateRange, selectedCompanyId, selectedCutId,
    showApiError, showNotification, translate,
  ]);

  return { navigateToDetail };
}

export default useMonthSummaryMarksDetailNavigation;
